import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import EnclosedSitesMultiSelect from '../../../../../../components/dialog-manager/dialogs/add-certificate-dialog/components/enclosed-sites-multi-select/EnclosedSitesMultiSelect';
import DownloadCertificateButton from '../../../../../../components/download-certificate-button/DownloadCertificateButton';
import EnclosedSitesGrid from '../../../../../../components/enclosed-sites-grid/EnclosedSitesGrid';
import {
  useGetCertificateDetailsById,
  useUpdateCertificateEnclosedSites,
} from '../../../../../../hooks/api/certificates';
import useNotificationContext from '../../../../../../hooks/notification/useNotificationContext';
import { Locale } from '../../../../../../i18n';
import { COMPANY_WIDE_VALUE } from '../../../../../../shared/constants/sites.constant';
import { ToastSeverity } from '../../../../../../shared/enums/toast-severity';
import { parseDateWithoutTime } from '../../../../../../shared/helpers/parse-date';
import { CertificateStatus as CertificateStatusEnum } from '../../../../../../shared/interfaces/ICertificate';
import CertificateStatusTag from '../../../../../certificates/components/certificate-status-tag/CertificateStatusTag';

type EditEnclosedSitesFormInput = {
  enclosedSites: (number | string)[];
};

const DetailsTab = (): JSX.Element => {
  const { certificateId } = useParams() as { certificateId: string };
  const [isEditingSites, setIsEditingSites] = useState(false);
  const queryClient = useQueryClient();
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const { notify } = useNotificationContext();

  const { data: certificate } = useGetCertificateDetailsById(
    { certificateId },
    {
      enabled: !!certificateId,
      retry: 0,
    },
  );

  const editSitesMethods = useForm<EditEnclosedSitesFormInput>({
    defaultValues: {
      enclosedSites: [],
    },
  });

  const selectedEnclosedSiteValues = editSitesMethods.watch('enclosedSites');

  const enclosedSiteIdsFromCertificate = useMemo(() => {
    if (certificate?.appliesToAllSites) {
      return [COMPANY_WIDE_VALUE];
    }

    return (certificate?.enclosedSites ?? []).map((site) => {
      return site.id;
    });
  }, [certificate]);

  useEffect(() => {
    editSitesMethods.reset({
      enclosedSites: enclosedSiteIdsFromCertificate,
    });
  }, [editSitesMethods, enclosedSiteIdsFromCertificate]);

  const { mutate: updateCertificateEnclosedSites, isLoading: isUpdatingEnclosedSites } =
    useUpdateCertificateEnclosedSites({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['certificate'] });
        notify({
          severity: ToastSeverity.SUCCESS,
          detail: t('certificateDetailsPage.tabs.detailsTab.toastMessages.sitesUpdate.success'),
          life: 3000,
        });
        setIsEditingSites(false);
      },
      onError: () => {
        notify({
          severity: ToastSeverity.ERROR,
          detail: t('certificateDetailsPage.tabs.detailsTab.toastMessages.sitesUpdate.failure'),
          life: 3000,
        });
      },
    });

  const handleCancelEditSites = () => {
    editSitesMethods.reset({
      enclosedSites: enclosedSiteIdsFromCertificate,
    });
    setIsEditingSites(false);
  };

  const handleSaveEditSites = () => {
    if (!certificate || !certificate.id) {
      return;
    }

    const appliesToAllSites = selectedEnclosedSiteValues.includes(COMPANY_WIDE_VALUE);
    const enclosedSiteIds = appliesToAllSites
      ? []
      : selectedEnclosedSiteValues.filter((v): v is number => {
          return typeof v === 'number';
        });

    updateCertificateEnclosedSites({
      certificateId: String(certificate.id),
      appliesToAllSites,
      enclosedSiteIds,
    });
  };

  return (
    <div className='flex flex-column gap-4 md:gap-6 mt-2 md:mt-4'>
      <div className='w-12 md:w-8'>
        {/* General details section */}
        <div className='flex flex-column gap-4'>
          <div className='flex flex-row gap-3 align-items-center'>
            <h4 className='m-0'>
              {t('certificateDetailsPage.tabs.detailsTab.sections.generalDetails.title')}
            </h4>
            <DownloadCertificateButton
              certificateId={certificate?.id}
              pdfPath={certificate?.s3Path}
            />
          </div>

          <div className='grid grid-nogutter gap-4 md:gap-0'>
            <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t(
                  'certificateDetailsPage.tabs.detailsTab.sections.generalDetails.certificateType',
                )}
              </span>
              <span className='font-light text-black-alpha-60'>{certificate?.type}</span>
            </div>

            <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t(
                  'certificateDetailsPage.tabs.detailsTab.sections.generalDetails.registrationNumber',
                )}
              </span>
              <span className='font-light text-black-alpha-60'>
                {certificate?.registrationNumber}
              </span>
            </div>

            <div className='col-12 md:col flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t('certificateDetailsPage.tabs.detailsTab.sections.generalDetails.documentId')}
              </span>
              <span className='font-light text-black-alpha-60'>{certificate?.documentId}</span>
            </div>
          </div>

          <div className='grid grid-nogutter gap-4 md:gap-0'>
            <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t('certificateDetailsPage.tabs.detailsTab.sections.generalDetails.validFrom')}
              </span>
              <span className='font-light text-black-alpha-60'>
                {parseDateWithoutTime(certificate?.validFrom, language as Locale)}
              </span>
            </div>

            <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t('certificateDetailsPage.tabs.detailsTab.sections.generalDetails.validUntil')}
              </span>
              <span className='font-light text-black-alpha-60'>
                {!certificate?.hasExpiry ? (
                  <span className='font-italic'>
                    {t(
                      'certificateDetailsPage.tabs.detailsTab.sections.generalDetails.noExpiration',
                    )}
                  </span>
                ) : (
                  parseDateWithoutTime(certificate?.validUntil, language as Locale)
                )}
              </span>
            </div>

            <div className='col-12 md:col flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t('certificateDetailsPage.tabs.detailsTab.sections.generalDetails.status')}
              </span>
              <CertificateStatusTag status={certificate?.status as CertificateStatusEnum} />
            </div>
          </div>
        </div>
      </div>

      <Divider className='p-0 m-0' />

      {/* Enclosed Sites Section */}
      <div className='flex flex-column gap-4 w-12 md:w-8'>
        <h4 className='m-0'>
          {t('certificateDetailsPage.tabs.detailsTab.sections.enclosedSites.title')}
        </h4>
        {!isEditingSites && (
          <>
            <EnclosedSitesGrid
              sites={certificate?.enclosedSites}
              emptyText={t(
                'certificateDetailsPage.tabs.detailsTab.sections.enclosedSites.emptyText',
              )}
            />
            {certificate?.canEditEnclosedSites && (
              <Button
                label={t('certificateDetailsPage.tabs.detailsTab.buttons.editSites')}
                outlined
                onClick={() => {
                  setIsEditingSites(true);
                }}
                className='w-fit'
              />
            )}
          </>
        )}
        {isEditingSites && (
          <FormProvider {...editSitesMethods}>
            <div className='flex flex-column'>
              <EnclosedSitesMultiSelect disabled={isUpdatingEnclosedSites} />
              <div className='flex flex-row gap-2'>
                <Button
                  label={t('certificateDetailsPage.tabs.detailsTab.buttons.saveSites')}
                  onClick={handleSaveEditSites}
                  loading={isUpdatingEnclosedSites}
                  disabled={isUpdatingEnclosedSites || selectedEnclosedSiteValues.length === 0}
                />
                <Button
                  label={t('certificateDetailsPage.tabs.detailsTab.buttons.cancelEditSites')}
                  outlined
                  onClick={handleCancelEditSites}
                  disabled={isUpdatingEnclosedSites}
                />
              </div>
            </div>
          </FormProvider>
        )}
      </div>
    </div>
  );
};

export default DetailsTab;
